import { inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { Ref } from 'vue';

import { FileDiff, type FileDiffOptions } from '../../components/FileDiff';
import { VirtualizedFileDiff } from '../../components/VirtualizedFileDiff';
import type { GetHoveredLineResult } from '../../managers/InteractionManager';
import type {
  DiffLineAnnotation,
  FileDiffMetadata,
  SelectedLineRange,
  VirtualFileMetrics,
} from '../../types';
import { areOptionsEqual } from '../../utils/areOptionsEqual';
import { noopRender } from '../constants';
import { VirtualizerInjectionKey, WorkerPoolInjectionKey } from '../injection';

export interface UseFileDiffInstanceParams {
  props: {
    fileDiff: FileDiffMetadata;
    options?: FileDiffOptions<undefined>;
    metrics?: VirtualFileMetrics;
    lineAnnotations?: DiffLineAnnotation[];
    selectedLines?: SelectedLineRange | null;
    prerenderedHTML?: string;
    disableWorkerPool: boolean;
  };
  hasCustomHeader(): boolean;
  hasGutterRenderer(): boolean;
}

export interface UseFileDiffInstanceReturn {
  containerRef: Ref<HTMLElement | null>;
  getHoveredLine(): GetHoveredLineResult<'diff'> | undefined;
}

// Owns the imperative `FileDiff`/`VirtualizedFileDiff` instance for a Vue
// component. Shared by FileDiff, MultiFileDiff, and PatchDiff (each computes a
// `FileDiffMetadata` and feeds it in via `props.fileDiff`). Mirrors the React
// `useFileDiffInstance` hook.
export function useFileDiffInstance({
  props,
  hasCustomHeader,
  hasGutterRenderer,
}: UseFileDiffInstanceParams): UseFileDiffInstanceReturn {
  const containerRef = ref<HTMLElement | null>(null);
  const virtualizer = inject(VirtualizerInjectionKey, undefined);
  const poolManager = inject(WorkerPoolInjectionKey, undefined);
  let instance: FileDiff | VirtualizedFileDiff | undefined;

  function buildOptions(): FileDiffOptions<undefined> | undefined {
    return mergeFileDiffOptions({
      options: props.options,
      controlledSelection: props.selectedLines !== undefined,
      hasCustomHeader: hasCustomHeader(),
      hasGutterRenderer: hasGutterRenderer(),
    });
  }

  onMounted(() => {
    const fileContainer = containerRef.value;
    if (fileContainer == null) {
      throw new Error(
        'useFileDiffInstance: container element missing on mount'
      );
    }
    const workerManager = props.disableWorkerPool ? undefined : poolManager;
    const options = buildOptions();
    instance =
      virtualizer != null
        ? new VirtualizedFileDiff(
            options,
            virtualizer,
            props.metrics,
            workerManager,
            true
          )
        : new FileDiff(options, workerManager, true);
    void instance.hydrate({
      fileDiff: props.fileDiff,
      fileContainer,
      lineAnnotations: props.lineAnnotations,
      prerenderedHTML: props.prerenderedHTML,
    });
  });

  watch(
    () =>
      [
        props.fileDiff,
        props.options,
        props.lineAnnotations,
        props.selectedLines,
        hasCustomHeader(),
        hasGutterRenderer(),
      ] as const,
    () => {
      if (instance == null) {
        return;
      }
      const nextOptions = buildOptions();
      const forceRender = !areOptionsEqual(instance.options, nextOptions);
      instance.setOptions(nextOptions);
      void instance.render({
        fileDiff: props.fileDiff,
        lineAnnotations: props.lineAnnotations,
        forceRender,
      });
      if (props.selectedLines !== undefined) {
        instance.setSelectedLines(props.selectedLines);
      }
    },
    { flush: 'post' }
  );

  onBeforeUnmount(() => {
    instance?.cleanUp();
    instance = undefined;
  });

  return {
    containerRef,
    getHoveredLine: () => instance?.getHoveredLine(),
  };
}

interface MergeFileDiffOptionsParams {
  options: FileDiffOptions<undefined> | undefined;
  controlledSelection: boolean;
  hasCustomHeader: boolean;
  hasGutterRenderer: boolean;
}

function mergeFileDiffOptions({
  options,
  controlledSelection,
  hasCustomHeader,
  hasGutterRenderer,
}: MergeFileDiffOptionsParams): FileDiffOptions<undefined> | undefined {
  if (!controlledSelection && !hasGutterRenderer && !hasCustomHeader) {
    return options;
  }
  return {
    ...options,
    controlledSelection,
    renderCustomHeader: hasCustomHeader
      ? noopRender
      : options?.renderCustomHeader,
    renderGutterUtility: hasGutterRenderer
      ? noopRender
      : options?.renderGutterUtility,
  };
}
